import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Users, Truck, Briefcase, Tag, Wrench, Settings, LogOut, BarChart3, Receipt, MessageSquare, Star, HelpCircle, MapPin, FileEdit } from "lucide-react";
import { api, clearToken, getToken } from "@/lib/api";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/admin", label: "Tableau de bord", Icon: LayoutDashboard, end: true },
  { to: "/admin/demandes", label: "Demandes", Icon: FileText },
  { to: "/admin/devis", label: "Devis", Icon: Receipt },
  { to: "/admin/messages", label: "Messages contact", Icon: MessageSquare },
  { to: "/admin/chauffeurs", label: "Chauffeurs", Icon: Truck },
  { to: "/admin/clients", label: "Clients", Icon: Users },
  { to: "/admin/partenaires", label: "Partenaires", Icon: Briefcase },
  { to: "/admin/tarifs", label: "Tarifs", Icon: Tag },
  { to: "/admin/services", label: "Services", Icon: Wrench },
  { to: "/admin/avis", label: "Avis clients", Icon: Star },
  { to: "/admin/faqs", label: "FAQ", Icon: HelpCircle },
  { to: "/admin/zones", label: "Zones", Icon: MapPin },
  { to: "/admin/contenu", label: "Contenu des pages", Icon: FileEdit },
  { to: "/admin/analytics", label: "Statistiques", Icon: BarChart3 },
  { to: "/admin/parametres", label: "Paramètres", Icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { clearToken(); navigate("/admin/login"); });
  }, [navigate]);

  const logout = () => { api.post("/auth/logout").finally(() => { clearToken(); navigate("/admin/login"); }); };

  if (!user) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Chargement...</div>;

  return (
    <div className="min-h-screen bg-slate-900 text-white flex">
      <aside className="w-60 shrink-0 bg-slate-800 border-r border-slate-700 hidden lg:flex flex-col">
        <Link to="/admin" className="block p-5 border-b border-slate-700">
          <Logo tone="light" className="h-8" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">Espace Admin</div>
        </Link>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-slate-300 hover:bg-slate-700"}`}
              data-testid={`adminnav-${label.toLowerCase()}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-700">
          <div className="text-xs text-slate-500">Connecté</div>
          <div className="font-semibold text-sm">{user.name}</div>
          <button onClick={logout} className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-2 text-sm transition-colors" data-testid="admin-logout-btn">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <div className="lg:hidden flex items-center justify-between px-4 h-14 bg-slate-800 border-b border-slate-700">
          <Link to="/admin" className="flex items-center gap-2"><Logo tone="light" className="h-6" /><span className="font-extrabold text-slate-400 text-xs">Admin</span></Link>
          <button onClick={logout} className="text-slate-400"><LogOut size={18} /></button>
        </div>
        <div className="lg:hidden flex overflow-x-auto no-scrollbar bg-slate-800 border-b border-slate-700">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-3 text-xs font-bold whitespace-nowrap ${isActive ? "text-brand border-b-2 border-brand" : "text-slate-400"}`}>
              <Icon size={14} />{label}
            </NavLink>
          ))}
        </div>
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
